import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { withFragments } from '@sorare/core/src/lib/gql';
import type { Color } from '@sorare/core/src/style/types';

import type { SteppedPrimaryOffer_primaryOffer } from './__generated__/steppedPrimaryOffer.graphql.ts';

type SteppedPrimaryOfferInput = {
  instantBuyCampaign?: {
    endDate?: Date | null;
    remainingAtCurrentPrice?: number | null;
    quantityPerStep?: number | null;
    soldOut?: boolean | null;
  } | null;
} | null;

type SteppedPrimaryOffer = {
  // Any live instant-buy campaign (crisp or stepped): drives the price lock + timer ribbon.
  isCampaign: boolean;
  // Stepped dynamic campaign only (has a per-step size): drives the supply bar/counter.
  isStepped: boolean;
  soldOut: boolean;
  remaining: number | null;
  ratio: number | null;
  color: Color;
  endDate: Date | null;
};

const SUPPLY_COLORS = {
  high: 'var(--c-score-mediumHigh)', // green
  medium: 'var(--c-score-mediumLow)', // gold
  low: 'var(--c-score-low)', // orange
  empty: 'var(--c-score-veryLow)', // red
} as const satisfies Record<string, Color>;

// Bands at midpoints between fifths: per step of 5, 5-4 left → green, 3 → gold, 2 → orange, 1-0 → red.
const getSupplyColor = (ratio: number): Color => {
  if (ratio >= 0.7) return SUPPLY_COLORS.high;
  if (ratio >= 0.5) return SUPPLY_COLORS.medium;
  if (ratio >= 0.3) return SUPPLY_COLORS.low;
  return SUPPLY_COLORS.empty;
};

export const getSteppedPrimaryOffer = withFragments(
  (offer: SteppedPrimaryOfferInput): SteppedPrimaryOffer => {
    const campaign = offer?.instantBuyCampaign;
    const remaining = campaign?.remainingAtCurrentPrice ?? null;
    const quantityPerStep = campaign?.quantityPerStep;
    const soldOut = !!campaign?.soldOut;
    const isCampaign = remaining != null || campaign?.soldOut != null;
    const isStepped = quantityPerStep != null && quantityPerStep > 0;

    const rawRatio =
      quantityPerStep != null && quantityPerStep > 0 && remaining != null
        ? Math.min(1, Math.max(0, remaining / quantityPerStep))
        : null;
    const ratio = soldOut ? 0 : rawRatio;

    return {
      isCampaign,
      isStepped,
      soldOut,
      remaining,
      ratio,
      color: getSupplyColor(ratio ?? 1),
      endDate: campaign?.endDate ?? null,
    };
  },
  {
    primaryOffer: gql`
      fragment SteppedPrimaryOffer_primaryOffer on TokenPrimaryOffer {
        id
        instantBuyCampaign {
          id
          endDate
          remainingAtCurrentPrice
          quantityPerStep
          soldOut
        }
      }
    ` as TypedDocumentNode<SteppedPrimaryOffer_primaryOffer>,
  }
);
